import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove,
  query,
  where,
  getDocs 
} from 'firebase/firestore';

const TeamManager = ({ onClose }) => {
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [sharedCards, setSharedCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeamName, setNewTeamName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (auth.currentUser) {
      setUser(auth.currentUser);
      loadUserTeams();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserTeams = async () => {
    try {
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists() && userDoc.data().teams) {
        const teamIds = userDoc.data().teams;
        const teamPromises = teamIds.map(teamId => 
          getDoc(doc(db, 'teams', teamId))
        );
        const teamDocs = await Promise.all(teamPromises);
        const teamsData = teamDocs
          .filter(doc => doc.exists())
          .map(doc => ({ id: doc.id, ...doc.data() }));
        
        setTeams(teamsData);
        if (teamsData.length > 0) {
          setSelectedTeam(teamsData[0]);
          loadTeamData(teamsData[0]);
        }
      }
    } catch (error) {
      console.error('載入團隊失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamData = async (team) => {
    try {
      // 載入團隊成員資訊
      const memberPromises = team.members.map(memberId => 
        getDoc(doc(db, 'users', memberId))
      );
      const memberDocs = await Promise.all(memberPromises);
      const members = memberDocs
        .filter(doc => doc.exists())
        .map(doc => ({ id: doc.id, ...doc.data() }));
      
      setTeamMembers(members);
      
      // 載入共享的名片
      const cardsQuery = query(
        collection(db, 'businessCardsnotenew'),
        where('sharedWithTeams', 'array-contains', team.id)
      );
      const cardsSnapshot = await getDocs(cardsQuery);
      const cards = cardsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setSharedCards(cards);
    } catch (error) {
      console.error('載入團隊資料失敗:', error);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    
    setCreating(true);
    try {
      const teamId = `team_${Date.now()}`;
      const teamData = {
        name: newTeamName,
        createdBy: user.uid,
        createdAt: new Date(),
        members: [user.uid],
        admins: [user.uid]
      };
      
      // 創建團隊
      await setDoc(doc(db, 'teams', teamId), teamData);
      
      // 更新用戶的團隊列表
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        teams: arrayUnion(teamId)
      });
      
      setNewTeamName('');
      await loadUserTeams();
      alert('團隊創建成功！');
    } catch (error) {
      console.error('創建團隊失敗:', error);
      alert('創建團隊失敗，請重試');
    } finally {
      setCreating(false);
    }
  };

  const inviteMember = async () => {
    if (!newMemberEmail.trim() || !selectedTeam) return;
    
    try {
      // 根據 email 查找用戶
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', newMemberEmail.trim())
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (usersSnapshot.empty) {
        alert('找不到該用戶，請確認 email 是否正確');
        return;
      }
      
      const userData = usersSnapshot.docs[0];
      const userId = userData.id;
      
      if (selectedTeam.members.includes(userId)) {
        alert('該用戶已經是團隊成員');
        return;
      }
      
      // 更新團隊成員列表
      const teamRef = doc(db, 'teams', selectedTeam.id);
      await updateDoc(teamRef, {
        members: arrayUnion(userId)
      });
      
      // 更新用戶的團隊列表
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        teams: arrayUnion(selectedTeam.id)
      });
      
      setNewMemberEmail('');
      await loadTeamData(selectedTeam);
      alert('成員邀請成功！');
    } catch (error) {
      console.error('邀請成員失敗:', error);
      alert('邀請成員失敗，請重試');
    }
  };

  const removeMember = async (memberId) => {
    if (!selectedTeam || !window.confirm('確定要移除此成員嗎？')) return;
    
    try {
      // 更新團隊成員列表
      const teamRef = doc(db, 'teams', selectedTeam.id);
      await updateDoc(teamRef, {
        members: arrayRemove(memberId),
        admins: arrayRemove(memberId)
      });
      
      // 更新用戶的團隊列表
      const userRef = doc(db, 'users', memberId);
      await updateDoc(userRef, {
        teams: arrayRemove(selectedTeam.id)
      });
      
      await loadTeamData(selectedTeam);
      alert('成員移除成功！');
    } catch (error) {
      console.error('移除成員失敗:', error);
      alert('移除成員失敗，請重試');
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading-message">載入中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>團隊管理</h2>
          <button onClick={onClose} className="close-btn">✕</button>
        </div>
        
        <div className="modal-body">
          {/* 團隊列表 */}
          <div className="section">
            <h3>我的團隊</h3>
            <div className="team-list">
              {teams.map(team => (
                <button
                  key={team.id}
                  onClick={() => {
                    setSelectedTeam(team);
                    loadTeamData(team);
                  }}
                  className={`team-item ${selectedTeam?.id === team.id ? 'active' : ''}`}
                >
                  {team.name}
                  <span className="member-count">{team.members.length} 成員</span>
                </button>
              ))}
            </div>
            
            {/* 創建新團隊 */}
            <div className="create-team">
              <input
                type="text"
                placeholder="輸入團隊名稱"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="team-input"
              />
              <button 
                onClick={createTeam}
                disabled={creating || !newTeamName.trim()}
                className="create-btn"
              >
                {creating ? '創建中...' : '創建團隊'}
              </button>
            </div>
          </div>

          {/* 團隊詳情 */}
          {selectedTeam && (
            <div className="section">
              <h3>{selectedTeam.name} - 成員管理</h3>
              
              {/* 邀請成員 */}
              <div className="invite-member">
                <input
                  type="email"
                  placeholder="輸入成員 Email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  className="member-input"
                />
                <button 
                  onClick={inviteMember}
                  disabled={!newMemberEmail.trim()}
                  className="invite-btn"
                >
                  邀請成員
                </button>
              </div>
              
              {/* 成員列表 */}
              <div className="members-list">
                {teamMembers.map(member => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <span className="member-name">{member.displayName || member.email}</span>
                      <span className="member-email">{member.email}</span>
                      {selectedTeam.admins.includes(member.id) && (
                        <span className="admin-badge">管理員</span>
                      )}
                    </div>
                    
                    {selectedTeam.admins.includes(user.uid) && member.id !== user.uid && (
                      <button
                        onClick={() => removeMember(member.id)}
                        className="remove-btn"
                      >
                        移除
                      </button>
                    )}
                  </div>
                ))}
              </div>
              
              {/* 共享名片 */}
              <div className="shared-cards">
                <h4>共享的名片 ({sharedCards.length})</h4>
                {sharedCards.length === 0 ? (
                  <p className="no-cards">目前沒有共享的名片</p>
                ) : (
                  <div className="cards-grid">
                    {sharedCards.map(card => (
                      <div key={card.id} className="card-item">
                        <div className="card-info">
                          <strong>{card['姓名'] || card['Name'] || '未知'}</strong>
                          <span>{card['公司名稱'] || card['Company'] || ''}</span>
                        </div>
                        <span className="shared-by">
                          由 {card.userName} 分享
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <style jsx="true">{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(5px);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 2000;
            padding: 20px;
          }

          .modal-content {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(20px);
            border-radius: 24px;
            border: 1px solid rgba(255, 255, 255, 0.6);
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            max-width: 900px;
            width: 100%;
            max-height: 90vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 28px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
          }

          .modal-header h2 {
            margin: 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 24px;
            font-weight: 600;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 20px;
            cursor: pointer;
            color: rgba(108, 99, 143, 0.6);
            padding: 8px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            color: rgba(108, 99, 143, 0.9);
          }

          .modal-body {
            padding: 28px;
            overflow-y: auto;
            flex: 1;
          }

          .section {
            margin-bottom: 32px;
          }

          .section h3 {
            margin: 0 0 16px 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 18px;
            font-weight: 600;
          }

          .team-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-bottom: 16px;
          }

          .team-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            background: rgba(255, 255, 255, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            text-align: left;
          }

          .team-item:hover {
            background: rgba(255, 255, 255, 0.4);
            transform: translateY(-1px);
          }

          .team-item.active {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-color: rgba(102, 126, 234, 0.6);
          }

          .member-count {
            font-size: 12px;
            opacity: 0.8;
          }

          .create-team, .invite-member {
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .team-input, .member-input {
            flex: 1;
            padding: 10px 14px;
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 10px;
            background: rgba(255, 255, 255, 0.3);
            backdrop-filter: blur(10px);
            font-size: 14px;
            color: rgba(108, 99, 143, 0.9);
            outline: none;
          }

          .team-input:focus, .member-input:focus {
            border-color: rgba(102, 126, 234, 0.6);
            box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
          }

          .create-btn, .invite-btn {
            padding: 10px 16px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            border-radius: 10px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .create-btn:hover, .invite-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
          }

          .create-btn:disabled, .invite-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .members-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin: 16px 0;
          }

          .member-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            background: rgba(255, 255, 255, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
          }

          .member-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .member-name {
            font-weight: 600;
            color: rgba(108, 99, 143, 0.9);
          }

          .member-email {
            font-size: 12px;
            color: rgba(108, 99, 143, 0.6);
          }

          .admin-badge {
            background: linear-gradient(135deg, #ffc107 0%, #ff8f00 100%);
            color: white;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 10px;
            font-weight: 600;
          }

          .remove-btn {
            background: rgba(244, 67, 54, 0.2);
            color: rgba(198, 40, 40, 0.9);
            border: 1px solid rgba(244, 67, 54, 0.3);
            padding: 6px 12px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 12px;
            transition: all 0.2s ease;
          }

          .remove-btn:hover {
            background: rgba(244, 67, 54, 0.3);
            transform: translateY(-1px);
          }

          .shared-cards h4 {
            margin: 0 0 12px 0;
            color: rgba(108, 99, 143, 0.9);
            font-size: 16px;
            font-weight: 600;
          }

          .no-cards {
            text-align: center;
            color: rgba(108, 99, 143, 0.6);
            font-style: italic;
          }

          .cards-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 12px;
          }

          .card-item {
            background: rgba(255, 255, 255, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            padding: 12px;
            transition: all 0.2s ease;
          }

          .card-item:hover {
            background: rgba(255, 255, 255, 0.4);
            transform: translateY(-1px);
          }

          .card-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-bottom: 8px;
          }

          .card-info strong {
            color: rgba(108, 99, 143, 0.9);
          }

          .card-info span {
            font-size: 12px;
            color: rgba(108, 99, 143, 0.6);
          }

          .shared-by {
            font-size: 10px;
            color: rgba(108, 99, 143, 0.5);
            font-style: italic;
          }

          .loading-message {
            text-align: center;
            padding: 40px;
            color: rgba(108, 99, 143, 0.8);
            font-size: 18px;
          }

          @media (max-width: 768px) {
            .modal-content {
              max-height: 95vh;
              margin: 10px;
            }

            .create-team, .invite-member {
              flex-direction: column;
              align-items: stretch;
            }

            .cards-grid {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default TeamManager;